import { Realm } from 'types';
import { SandboxStatusTypes } from 'SandboxModule/constants';
import { differenceInHours } from 'date-fns';

export const checkIsSandboxCreating = (status: string) => {
  return status === SandboxStatusTypes.Creating;
};

export const checkIsSandboxInitialized = (status: string) => {
  return status === SandboxStatusTypes.Initialized;
};

export const checkIsSandboxNotInitialized = (status: string) => {
  return status === SandboxStatusTypes.NotInitialized;
};

export const checkIsSandboxFailed = (status: string) => {
  return status === SandboxStatusTypes.Failed;
};

export const checkIsSandboxArchived = (status: string) => {
  return status === SandboxStatusTypes.Archived;
};

export const checkIsSandboxRemoved = (status: string) => {
  return status === SandboxStatusTypes.Removed;
};

/**
 * Destroy (and Retry Destroy on the Archived tab) is offered for every status
 * except the two where there is nothing to act on:
 * - Creating: provisioning is still in flight (unless it has been stuck > 2h)
 * - Removed: teardown already finished, no resources are left to destroy
 *
 * Archived and Partially Removed stay destroyable — a teardown that stalled
 * part-way is exactly the case the Archived tab exists to retry.
 */
export const checkIsSandboxDestroyable = ({
  status,
  created_at,
}: {
  status: Realm['status'];
  created_at: Realm['created_at'];
}) => {
  if (checkIsSandboxRemoved(status)) return false;

  const isCreating = checkIsSandboxCreating(status);
  const isCreatingMoreThanTwoHours =
    isCreating && differenceInHours(new Date(), new Date(created_at)) > 2;

  return !isCreating || isCreatingMoreThanTwoHours;
};

/**
 * Statuses where the Initialize action should be offered (GLX-1738): only
 * realms that failed to initialize properly and need a retry.
 *
 * Includes:
 * - ReleaseTaskFailed: Release tasks failed, initialization not yet attempted
 * - InitializationFailed: Initialization failed, can retry
 * - PartiallyInitialized: Some services initialized, can complete
 *
 * Excludes:
 * - NotInitialized: Normal pre-initialization lifecycle state, not a failure
 * - Failed: Infrastructure failure during creation, not recoverable via initialization
 * - Creating: Still being provisioned
 * - Initialized: Already complete
 * - Archived/PartiallyRemoved/Removed: End-of-life states
 */
const initializableStatuses = new Set([
  SandboxStatusTypes.ReleaseTaskFailed,
  SandboxStatusTypes.InitializationFailed,
  SandboxStatusTypes.PartiallyInitialized,
]);

export const checkIsSandboxInitializable = (status: string) => {
  return initializableStatuses.has(status as SandboxStatusTypes);
};

// Accessible = Not creating and is already initialized
export const checkIsSandboxAccessible = (status: string) => {
  return checkIsSandboxInitialized(status);
};
